import type { TaxCaseStatus } from '../../types/taxCase';

interface PipelineStep {
  status: TaxCaseStatus;
  label: string;
  description: string;
  icon: string;
}

const PIPELINE_STEPS: PipelineStep[] = [
  {
    status: 'ONBOARDING',
    label: 'Onboarding',
    description: 'Client setup and initial consultation',
    icon: 'person_add'
  },
  {
    status: 'DOC_REQUEST',
    label: 'Document Request',
    description: 'Requesting required tax documents',
    icon: 'request_page'
  },
  {
    status: 'UNDER_REVIEW',
    label: 'Under Review',
    description: 'Reviewing submitted documents',
    icon: 'rate_review'
  },
  {
    status: 'CALCULATION',
    label: 'Calculation',
    description: 'Preparing tax calculations',
    icon: 'calculate'
  },
  {
    status: 'PENDING_SIGNATURE',
    label: 'Pending Signature',
    description: 'Awaiting client signature',
    icon: 'draw'
  },
  {
    status: 'AWAITING_PAYMENT',
    label: 'Awaiting Payment',
    description: 'Invoice sent, awaiting payment',
    icon: 'payments'
  },
  {
    status: 'COMPLETED',
    label: 'Completed',
    description: 'Tax filing completed successfully',
    icon: 'check_circle'
  }
];

interface PipelineTrackerProps {
  currentStatus: TaxCaseStatus;
  allowedTransitions?: TaxCaseStatus[];
  onStatusChange?: (status: TaxCaseStatus) => void;
  isLoading?: boolean;
}

export function PipelineTracker({ 
  currentStatus, 
  allowedTransitions = [], 
  onStatusChange,
  isLoading = false 
}: PipelineTrackerProps) {
  const currentIndex = PIPELINE_STEPS.findIndex(step => step.status === currentStatus);

  const getStepState = (stepIndex: number) => {
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  const isTransitionAllowed = (status: TaxCaseStatus) => {
    return allowedTransitions.includes(status);
  };

  return (
    <div className="bg-surface-container-low rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-primary">Case Pipeline</h3>
          <p className="text-on-surface/60 text-sm mt-1">
            Track progress through the tax preparation workflow
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-3 h-3 bg-primary rounded-full"></span>
          <span className="text-on-surface/60">Current Stage</span>
        </div>
      </div>

      {/* Desktop Pipeline */}
      <div className="hidden lg:block">
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-6 left-6 right-6 h-0.5 bg-surface-container-highest">
            <div 
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${(currentIndex / (PIPELINE_STEPS.length - 1)) * 100}%` }}
            />
          </div>

          {/* Steps */}
          <div className="grid grid-cols-7 gap-4">
            {PIPELINE_STEPS.map((step, index) => {
              const state = getStepState(index);
              const isClickable = onStatusChange && isTransitionAllowed(step.status);
              
              return (
                <div key={step.status} className="relative">
                  <button
                    onClick={() => isClickable ? onStatusChange(step.status) : undefined}
                    disabled={isLoading || !isClickable}
                    className={`w-full text-center group ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    {/* Step Circle */}
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 transition-all duration-300
                      ${state === 'completed' 
                        ? 'bg-primary text-white' 
                        : state === 'current'
                        ? 'bg-primary text-white ring-4 ring-primary/20'
                        : 'bg-surface-container-high text-on-surface/40'
                      }
                      ${isClickable ? 'group-hover:scale-110 group-hover:shadow-lg' : ''}
                    `}>
                      <span className="material-symbols-outlined text-xl">
                        {state === 'completed' ? 'check' : step.icon}
                      </span>
                    </div>

                    {/* Step Label */}
                    <div className="space-y-1">
                      <p className={`
                        text-sm font-semibold transition-colors
                        ${state === 'current' 
                          ? 'text-primary' 
                          : state === 'completed'
                          ? 'text-on-surface'
                          : 'text-on-surface/60'
                        }
                      `}>
                        {step.label}
                      </p>
                      <p className="text-xs text-on-surface/40 leading-tight">
                        {step.description}
                      </p>
                    </div>

                    {/* Current Indicator */}
                    {state === 'current' && (
                      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                        <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                      </div>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile Pipeline */}
      <div className="lg:hidden space-y-4">
        {PIPELINE_STEPS.map((step, index) => {
          const state = getStepState(index);
          const isClickable = onStatusChange && isTransitionAllowed(step.status);
          
          return (
            <button
              key={step.status}
              onClick={() => isClickable ? onStatusChange(step.status) : undefined}
              disabled={isLoading || !isClickable}
              className={`
                w-full flex items-center gap-4 p-4 rounded-xl transition-all
                ${state === 'current' 
                  ? 'bg-primary/10 border-2 border-primary' 
                  : 'bg-surface-container hover:bg-surface-container-high'
                }
                ${isClickable ? 'cursor-pointer' : 'cursor-default'}
              `}
            >
              {/* Step Circle */}
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                ${state === 'completed' 
                  ? 'bg-primary text-white' 
                  : state === 'current'
                  ? 'bg-primary text-white'
                  : 'bg-surface-container-high text-on-surface/40'
                }
              `}>
                <span className="material-symbols-outlined text-lg">
                  {state === 'completed' ? 'check' : step.icon}
                </span>
              </div>

              {/* Step Content */}
              <div className="flex-1 text-left">
                <p className={`
                  font-semibold
                  ${state === 'current' 
                    ? 'text-primary' 
                    : state === 'completed'
                    ? 'text-on-surface'
                    : 'text-on-surface/60'
                  }
                `}>
                  {step.label}
                </p>
                <p className="text-sm text-on-surface/60 mt-1">
                  {step.description}
                </p>
              </div>

              {/* Arrow */}
              {isClickable && (
                <span className="material-symbols-outlined text-on-surface/40">
                  chevron_right
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-surface/80 rounded-2xl flex items-center justify-center">
          <div className="flex items-center gap-2 text-primary">
            <span className="material-symbols-outlined animate-spin">refresh</span>
            <span className="font-medium">Updating status...</span>
          </div>
        </div>
      )}
    </div>
  );
}